// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { StreamEndReason, StreamLimits } from './types.js';

const MAX_RETRIES = 8;
const BASE_RETRY_DELAY_MS = 500;
const MAX_IN_FLIGHT = 2;

interface PendingChunk {
  seq: number;
  bytes: ArrayBuffer;
  chunkTs: number;
  retryCount: number;
}

export interface InitSessionResponse {
  session_id: string;
  status: string;
  limits: StreamLimits;
}

/**
 * Handles all network I/O for a Stream session: init, chunk upload (queued,
 * ack-based eviction, retry with backoff+jitter, respects Retry-After, max 2
 * in-flight), and end. Every method swallows its own errors — a down/slow
 * ingest must never throw into the host app (same contract as
 * @biwave/core's Transport for Trace/Log/Pulse).
 */
export class StreamTransport {
  private readonly queue: PendingChunk[] = [];
  private inFlight = 0;

  constructor(
    private readonly baseUrl: string,
    private readonly clientId: string,
  ) {}

  async initSession(body: Record<string, unknown>): Promise<InitSessionResponse | null> {
    try {
      const res = await fetch(`${this.baseUrl}/stream/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Biwave-Client-Id': this.clientId },
        body: JSON.stringify(body),
      });
      if (!res.ok) return null;
      return (await res.json()) as InitSessionResponse;
    } catch {
      return null;
    }
  }

  /** Queues a chunk for upload — returns immediately, delivery/retry happens in the background. */
  enqueueChunk(sessionId: string, seq: number, bytes: ArrayBuffer, chunkTs: number): void {
    this.queue.push({ seq, bytes, chunkTs, retryCount: 0 });
    this.drain(sessionId);
  }

  get pendingCount(): number {
    return this.queue.length + this.inFlight;
  }

  private drain(sessionId: string): void {
    while (this.inFlight < MAX_IN_FLIGHT && this.queue.length > 0) {
      const chunk = this.queue.shift();
      if (!chunk) break;
      this.inFlight++;
      void this.send(sessionId, chunk).finally(() => {
        this.inFlight--;
        this.drain(sessionId);
      });
    }
  }

  private async send(sessionId: string, chunk: PendingChunk): Promise<void> {
    try {
      const res = await fetch(`${this.baseUrl}/stream/sessions/${sessionId}/chunks/${chunk.seq}`, {
        method: 'PUT',
        headers: {
          'X-Biwave-Client-Id': this.clientId,
          'X-Biwave-Capture-Mode': 'dom',
          'X-Biwave-Chunk-Ts': String(chunk.chunkTs),
          'Content-Type': 'application/json',
        },
        // Wrapped in a Blob — some TS/lib.dom versions don't structurally
        // accept a typed array as BodyInit even though fetch() supports it
        // natively; a Blob sidesteps that entirely.
        body: new Blob([chunk.bytes]),
      });

      // 2xx: acked, already evicted (shift()'d out of the queue in drain()).
      // 400/413: permanent — content is invalid or oversized, retrying won't help.
      if (res.ok || res.status === 400 || res.status === 413) return;

      this.retryOrDrop(sessionId, chunk, res.headers.get('Retry-After'));
    } catch {
      this.retryOrDrop(sessionId, chunk, null);
    }
  }

  private retryOrDrop(
    sessionId: string,
    chunk: PendingChunk,
    retryAfterHeader: string | null,
  ): void {
    if (chunk.retryCount >= MAX_RETRIES) return; // drop this seq, don't hold up the rest

    const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;
    const backoff = BASE_RETRY_DELAY_MS * 2 ** chunk.retryCount;
    const jitter = Math.random() * backoff * 0.3;
    const delay = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1000 : backoff + jitter;

    setTimeout(() => {
      this.queue.push({ ...chunk, retryCount: chunk.retryCount + 1 });
      this.drain(sessionId);
    }, delay);
  }

  /**
   * Fire-and-forget session-events batch (console/network/click/navigation).
   * Deliberately no retry queue — the contract with the backend is
   * best-effort: duplicates are tolerated, losses are acceptable, and a
   * failed batch must never build up memory or traffic pressure.
   */
  sendEvents(sessionId: string, events: unknown[], keepalive = false): void {
    if (events.length === 0) return;
    const url = `${this.baseUrl}/stream/sessions/${sessionId}/events`;
    const body = JSON.stringify({ client_id: this.clientId, events });

    if (keepalive) {
      this.sendKeepalive(url, 'POST', body);
      return;
    }

    try {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Biwave-Client-Id': this.clientId },
        body,
      }).catch(() => {
        // best-effort — swallow
      });
    } catch {
      // swallow synchronous throws too
    }
  }

  endSession(
    sessionId: string,
    body: { ended_at: number; last_seq: number; chunk_count: number; reason: StreamEndReason },
  ): void {
    this.sendKeepalive(
      `${this.baseUrl}/stream/sessions/${sessionId}/end`,
      'POST',
      JSON.stringify(body),
    );
  }

  /**
   * Best-effort chunk send for unload time (visibilitychange/pagehide) —
   * bypasses the retry queue entirely since there's no more page left to
   * retry from; either it lands or the backend's idle sweeper finalizes the
   * session without it.
   */
  sendChunkKeepalive(sessionId: string, seq: number, bytes: ArrayBuffer, chunkTs: number): void {
    this.sendKeepalive(
      `${this.baseUrl}/stream/sessions/${sessionId}/chunks/${seq}`,
      'PUT',
      new Blob([bytes]),
      { 'X-Biwave-Capture-Mode': 'dom', 'X-Biwave-Chunk-Ts': String(chunkTs) },
    );
  }

  /**
   * navigator.sendBeacon() can't set custom headers, so it can't carry
   * X-Biwave-Client-Id — using it would silently fail auth. fetch's
   * `keepalive: true` does support headers and survives page unload in all
   * evergreen browsers, so it's used here instead for every unload-time
   * send (never blocks the page from closing; failures are swallowed).
   */
  private sendKeepalive(
    url: string,
    method: 'POST' | 'PUT',
    body: BodyInit,
    extraHeaders: Record<string, string> = {},
  ): void {
    try {
      void fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Biwave-Client-Id': this.clientId,
          ...extraHeaders,
        },
        body,
        keepalive: true,
      }).catch(() => {
        // best-effort — swallow, the backend's idle sweeper will finalize the session anyway
      });
    } catch {
      // swallow synchronous throws too (e.g. an unsupported `keepalive` option in an old browser)
    }
  }
}
